import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';
import styledC, { css } from 'styled-components';

import { ArrowRightIcon, CalendarFilledIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import FieldValue from './FieldValue';
import ToggleIcon from './ToggleIcon';

type FieldProps = StylableComponent<HTMLButtonElement> & {
  open?: boolean;
  value?:
    | string
    | null
    | [string | null | undefined, string | null | undefined];
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  disabled?: boolean;
  error?: boolean;
  range?: boolean;
  placeholder?: ReactNode;
};

const StyledButton = styledC.button<Pick<FieldProps, 'error' | 'disabled'>>`
  display: flex;
  height: 36px;
  width: 300px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  padding: 0px ${theme.spacing.S}px;
  align-items: center;
  outline: none;
  background-color: ${theme.tokens.colors.background.primary.value};
  border: 1px solid ${theme.tokens.colors.border.value};
  border-radius: ${theme.borderRadius.small}px;
  transition: ${theme.transitions.create(['box-shadow', 'border-color'])};

  &:hover {
    box-shadow: ${theme.halos.hover};
  }

  &:focus {
    box-shadow: ${theme.halos.focus};
    border-color: ${theme.tokens.colors.border.focused.value};
  }

  &:active {
    box-shadow: ${theme.halos.active};
    border-color: ${theme.tokens.colors.border.value};
  }

  & svg {
    color: ${({ disabled }) =>
      disabled
        ? theme.tokens.colors.icon.inactive.value
        : theme.tokens.colors.icon.secondary.value};
  }

  ${({ disabled }) =>
    disabled
      ? css`
          background-color: ${theme.tokens.colors.background.neutral.inactive
            .value};
          border-color: ${theme.tokens.colors.border.inactive.value};
          &:hover, &:focus, &:active: {
            border-color: ${theme.tokens.colors.border.inactive.value};
            box-shadow: none;
          }
        `
      : ''}

  ${({ error }) =>
    error
      ? css`
          border-color: ${theme.tokens.colors.border.error.value};
          &:hover, &:focus, &:active: {
            border-color: ${theme.tokens.colors.border.error.value};
          }
        `
      : ''}
`;

const StyledFieldValue = styledC(FieldValue)`
  margin: 0 10px;
  display: block;
  ${theme.typography.ellipsis}
`;

const Field = React.forwardRef<HTMLButtonElement, FieldProps>(
  (
    {
      open,
      value,
      placeholder,
      startDatePlaceholder,
      endDatePlaceholder,
      disabled,
      error,
      range,
      style,
      ...props
    },
    ref,
  ) => {
    const [startDate, endDate] = Array.isArray(value) ? value : [value];
    const rangeDatesSelected = range ? startDate && endDate : false;

    return (
      <StyledButton
        type="button"
        error={error}
        disabled={disabled}
        ref={ref}
        css={style}
        {...props}
      >
        <CalendarFilledIcon size={16} />
        <StyledFieldValue
          value={startDate}
          disabled={disabled}
          selecting={open && !rangeDatesSelected}
          placeholder={range ? startDatePlaceholder : placeholder}
          data-testid="DatePickerFieldValue-start"
        />
        {range && (
          <>
            <ArrowRightIcon />
            <StyledFieldValue
              value={endDate}
              disabled={disabled}
              selecting={open && !rangeDatesSelected}
              placeholder={endDatePlaceholder}
              data-testid="DatePickerFieldValue-end"
            />
          </>
        )}
        <ToggleIcon isOpen={!!open} />
      </StyledButton>
    );
  },
);

Field.displayName = 'Field';

Field.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value to display, should be the formatted date(s) string(s) */
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  /** Placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Passed to the field, startDatePlaceholder to display when there is no value */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, endDatePlaceHolder to display when there is no value */
  endDatePlaceholder: PropTypes.string,
  /** Indicates whether the overlay is open */
  open: PropTypes.bool,
  /** Whether we should display range state */
  range: PropTypes.bool,
  /** Whether the field is disabled */
  disabled: PropTypes.bool,
  /** Whether the field should display error state */
  error: PropTypes.bool,
};

export default Field;
